import { useLocation } from 'react-router-dom';
import { Edit, SaveButton, SimpleForm, useInput } from 'react-admin';
import { MenuItem, Select } from '@mui/material';
import { CustomerFoodPreference } from '../../enums/CustomerFoodPreference';
import { CustomerGender } from '../../enums/CustomerGender';
import {
  BlueTOpBorderContainer,
  FormFieldGroup,
  FormGrid,
  FormLabel,
  SectionTitleRow,
  StyledTextInput,
  WorkspaceContainer,
  WorkspaceHeader,
  WorkspaceIconBadge,
  WorkspacePanel,
  WorkspaceTitle,
} from '../../styles/commonStyles';
import { EditIcon } from '../Svg';

const genderChoices = Object.values(CustomerGender).map((v) => ({ id: v, name: v }));
const foodPrefChoices = Object.values(CustomerFoodPreference).map((v) => ({ id: v, name: v }));

const selectSx = {
  width: '100%',
  border: '1px solid #D0D5DD',
  borderRadius: '6px',
  backgroundColor: '#FAFAFA',
  fontSize: '14px',
  '& fieldset': { display: 'none' },
  '& .MuiSelect-select': {
    padding: '12px 14px',
    '&:hover': { backgroundColor: '#F1F3F5' },
  },
};

interface ChoiceSelectProps {
  source: string;
  choices: { id: string; name: string }[];
}

const ChoiceSelect = ({ source, choices }: ChoiceSelectProps) => {
  const { field } = useInput({ source });
  return (
    <Select {...field} value={field.value ?? ''} displayEmpty={false} fullWidth sx={selectSx}>
      {choices.map((c) => (
        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
      ))}
    </Select>
  );
};

const CustomerEditToolbar = () => (
  <BlueTOpBorderContainer>
    <SaveButton
      label="Save Changes"
      sx={{
        backgroundColor: 'rgba(25, 117, 210, 0.9)',
        color: '#fff',
        padding: '6px 32px',
        borderRadius: '8px',
        fontWeight: 600,
        '&:hover': { backgroundColor: 'rgba(25, 117, 210, 1)' },
      }}
    />
  </BlueTOpBorderContainer>
);

const CustomerEdit = (props: any) => {
  const location = useLocation();
  const redirectTo = (location.state as any)?.from === 'show' ? 'show' : 'list';

  return (
    <WorkspaceContainer>
      <WorkspaceHeader>
        <SectionTitleRow>
          <WorkspaceIconBadge>
            <EditIcon />
          </WorkspaceIconBadge>
          <div>
            <WorkspaceTitle>Edit Customer</WorkspaceTitle>
          </div>
        </SectionTitleRow>
      </WorkspaceHeader>

      <Edit
        {...props}
        redirect={redirectTo}
        sx={{
          '& .RaEdit-card': {
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
          },
        }}
      >
        <SimpleForm toolbar={<CustomerEditToolbar />} sx={{ padding: 0 }}>
          <WorkspacePanel>

            <FormGrid style={{ marginTop: 0 }}>
              <FormFieldGroup>
                <FormLabel>First Name</FormLabel>
                <StyledTextInput source="fname" label={false} variant="outlined" fullWidth />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Last Name</FormLabel>
                <StyledTextInput source="lname" label={false} variant="outlined" fullWidth />
              </FormFieldGroup>
            </FormGrid>

            <FormGrid style={{ marginTop: '12px' }}>
              <FormFieldGroup>
                <FormLabel>Middle Name</FormLabel>
                <StyledTextInput source="mname" label={false} variant="outlined" fullWidth />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Email</FormLabel>
                <StyledTextInput source="email" label={false} variant="outlined" fullWidth />
              </FormFieldGroup>
            </FormGrid>

            <FormGrid style={{ marginTop: '12px' }}>
              <FormFieldGroup>
                <FormLabel>Gender</FormLabel>
                <ChoiceSelect source="gender" choices={genderChoices} />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Food Preference</FormLabel>
                <ChoiceSelect source="foodPreference" choices={foodPrefChoices} />
              </FormFieldGroup>
            </FormGrid>

            <FormGrid style={{ marginTop: '12px', gridTemplateColumns: '1fr' }}>
              <FormFieldGroup>
                <FormLabel>Address</FormLabel>
                <StyledTextInput source="address" label={false} variant="outlined" fullWidth />
              </FormFieldGroup>
            </FormGrid>

          </WorkspacePanel>
        </SimpleForm>
      </Edit>
    </WorkspaceContainer>
  );
};

export default CustomerEdit;
